import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import useNewsAdminServer from "./useNewsAdminServer";
import { fetchAdminHomeMainNotes, fetchAdminNoteByTitle, fetchAdminReplacementNotes, updateAdminHomeOrder } from "./api";

const HOME_SLOTS = 6;

function createHomeSlots(items = []) {
    const sortedItems = [...items]
        .sort((leftItem, rightItem) => (leftItem.orderHome || 0) - (rightItem.orderHome || 0))
        .slice(0, HOME_SLOTS);

    return [
        ...sortedItems,
        ...Array.from({ length: Math.max(0, HOME_SLOTS - sortedItems.length) }, () => null),
    ];
}

function moveHomeSlot(items, fromIndex, toIndex) {
    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);

    nextItems.splice(toIndex, 0, movedItem);

    return nextItems;
}

function formatLibraryCategoryLabel(value) {
    return String(value || "Sin categoria").replace(/-/g, " ");
}

export default function FispTvAdminEditHome() {
    const { server, serverResolved } = useNewsAdminServer();
    const [originalItems, setOriginalItems] = useState([]);
    const [draftItems, setDraftItems] = useState([]);
    const [activeSlotIndex, setActiveSlotIndex] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [libraryItems, setLibraryItems] = useState([]);
    const [libraryPage, setLibraryPage] = useState(0);
    const [libraryHasMore, setLibraryHasMore] = useState(true);
    const [status, setStatus] = useState("loading");
    const [libraryStatus, setLibraryStatus] = useState("idle");
    const [selectingCandidateId, setSelectingCandidateId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);

    useEffect(() => {
        if (!serverResolved) {
            return undefined;
        }

        const controller = new AbortController();
        setStatus("loading");
        setFeedback(null);

        fetchAdminHomeMainNotes(server, controller.signal)
            .then((items) => {
                setOriginalItems(items);
                setDraftItems(createHomeSlots(items));
                setStatus("ready");
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setOriginalItems([]);
                setDraftItems([]);
                setStatus("error");
            });

        return () => controller.abort();
    }, [server, serverResolved]);

    useEffect(() => {
        if (activeSlotIndex === null || !serverResolved || libraryItems.length) {
            return undefined;
        }

        const controller = new AbortController();
        setLibraryStatus("loading");

        fetchAdminReplacementNotes(server, controller.signal, 1)
            .then((items) => {
                setLibraryItems(items);
                setLibraryPage(1);
                setLibraryHasMore(items.length >= 20);
                setLibraryStatus("ready");
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setLibraryItems([]);
                setLibraryPage(0);
                setLibraryHasMore(false);
                setLibraryStatus("error");
            });

        return () => controller.abort();
    }, [activeSlotIndex, libraryItems.length, server, serverResolved]);

    const nextPage = useMemo(() => libraryPage + 1, [libraryPage]);
    const canSave = draftItems.length === HOME_SLOTS && draftItems.every(Boolean);

    const loadMoreLibraryItems = async () => {
        if (!libraryHasMore || libraryStatus === "loading-more" || !serverResolved) {
            return;
        }

        setLibraryStatus("loading-more");

        try {
            const items = await fetchAdminReplacementNotes(server, undefined, nextPage);

            setLibraryItems((currentItems) => {
                const mergedItems = [...currentItems];
                const seenKeys = new Set(currentItems.map((item) => `${item.id}-${item.slug}`));

                items.forEach((item) => {
                    const itemKey = `${item.id}-${item.slug}`;

                    if (!seenKeys.has(itemKey)) {
                        seenKeys.add(itemKey);
                        mergedItems.push(item);
                    }
                });

                return mergedItems;
            });
            setLibraryPage(nextPage);
            setLibraryHasMore(items.length >= 20);
            setLibraryStatus("ready");
        } catch {
            setLibraryStatus("error");
        }
    };

    const handlePickReplacement = async (candidate) => {
        if (activeSlotIndex === null) {
            return;
        }

        const isUsedInAnotherSlot = draftItems.some((item, index) => index !== activeSlotIndex && item?.id === candidate.id);

        if (isUsedInAnotherSlot) {
            setFeedback({ type: "error", message: "Esa nota ya esta elegida en otra posicion del home." });
            return;
        }

        setSelectingCandidateId(candidate.id ?? candidate.slug ?? candidate.title);

        try {
            const note = await fetchAdminNoteByTitle(candidate.title, server);
            const normalized = note?.normalized || null;

            setDraftItems((currentItems) =>
                currentItems.map((item, index) =>
                    index === activeSlotIndex
                        ? {
                            ...candidate,
                            image: normalized?.image || candidate.image || "",
                            orderHome: index + 1,
                        }
                        : item
                )
            );
            setActiveSlotIndex(null);
            setFeedback(null);
        } catch {
            setFeedback({ type: "error", message: "No pudimos cargar la imagen de esa nota." });
        } finally {
            setSelectingCandidateId(null);
        }
    };

    const handleRemoveSlot = (slotIndex) => {
        setDraftItems((currentItems) => currentItems.map((item, index) => (index === slotIndex ? null : item)));
        setFeedback(null);

        if (activeSlotIndex === slotIndex) {
            setActiveSlotIndex(null);
        }
    };

    const handleDragStart = (slotIndex) => {
        if (!draftItems[slotIndex]) {
            return;
        }

        setDragIndex(slotIndex);
        setDragOverIndex(slotIndex);
    };

    const handleDropOnSlot = (slotIndex) => {
        if (dragIndex === null) {
            return;
        }

        if (dragIndex === slotIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }

        setDraftItems((currentItems) => moveHomeSlot(currentItems, dragIndex, slotIndex));
        setDragIndex(null);
        setDragOverIndex(null);
        setFeedback(null);
    };

    const handleSave = async () => {
        if (!canSave) {
            setFeedback({ type: "error", message: "Completa las 6 posiciones antes de guardar." });
            return;
        }

        setSaving(true);
        setFeedback(null);

        try {
            await Promise.all(
                originalItems.map((item) =>
                    updateAdminHomeOrder(
                        {
                            idnota: item.id,
                            presenteHome: 0,
                            ordenHome: 0,
                        },
                        server
                    )
                )
            );

            await Promise.all(
                draftItems.map((item, index) =>
                    updateAdminHomeOrder(
                        {
                            idnota: item.id,
                            presenteHome: 1,
                            ordenHome: index + 1,
                        },
                        server
                    )
                )
            );

            const nextSnapshot = draftItems.map((item, index) => ({
                ...item,
                orderHome: index + 1,
            }));

            setOriginalItems(nextSnapshot);
            setDraftItems(createHomeSlots(nextSnapshot));
            setFeedback({ type: "success", message: "La portada se actualizo correctamente." });
        } catch {
            setFeedback({ type: "error", message: "No pudimos guardar los cambios del home." });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout title="Editar home - Hoy/mañana lo habilito" pageClassName="tv-admin-home-page">
            {/* {status === "loading" ? (
                <div className="tv-admin-fullscreen-spinner" aria-live="polite" aria-busy="true">
                    <span className="tv-admin-spinner" aria-hidden="true"></span>
                </div>
            ) : null}

            {status === "error" ? (
                <section className="tv-admin-placeholder-card is-home">
                    <h2>No pudimos cargar el home</h2>
                    <p>Hubo un problema al consultar las notas principales de la portada.</p>
                </section>
            ) : null}

            {status === "ready" ? (
                <>
                    {feedback ? <div className={`tv-admin-feedback is-${feedback.type}`}>{feedback.message}</div> : null}

                    <section className="tv-admin-home-stage">
                        <div className="tv-admin-home-grid">
                            {draftItems.map((item, index) => {
                                const isDragOver = dragOverIndex === index && dragIndex !== null && dragIndex !== index;

                                return (
                                    <div
                                        key={`home-slot-${index + 1}`}
                                        className={`tv-admin-home-card${item ? "" : " is-empty"}${isDragOver ? " is-drag-over" : ""}`}
                                        draggable={Boolean(item)}
                                        onDragStart={() => handleDragStart(index)}
                                        onDragEnd={() => {
                                            setDragIndex(null);
                                            setDragOverIndex(null);
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();

                                            if (dragIndex !== null) {
                                                setDragOverIndex(index);
                                            }
                                        }}
                                        onDrop={(event) => {
                                            event.preventDefault();
                                            handleDropOnSlot(index);
                                        }}
                                    >
                                        {item ? (
                                            <>
                                                <span className="tv-admin-home-card-thumb">
                                                    {item.image ? <img src={item.image} alt={item.title} /> : <span className="tv-admin-home-card-thumb-fallback"></span>}
                                                </span>

                                                <span className="tv-admin-home-card-copy">
                                                    <small>Posicion {index + 1}</small>
                                                    <strong>{item.title}</strong>
                                                </span>

                                                <span className="tv-admin-home-card-tools">
                                                    <span className="tv-admin-home-card-handle" aria-hidden="true">
                                                        &#8942;&#8942;
                                                    </span>

                                                    <button
                                                        type="button"
                                                        className="tv-admin-home-card-remove"
                                                        aria-label={`Quitar nota de la posicion ${index + 1}`}
                                                        onClick={() => handleRemoveSlot(index)}
                                                    >
                                                        &times;
                                                    </button>
                                                </span>
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className="tv-admin-home-card-empty-action"
                                                onClick={() => {
                                                    setActiveSlotIndex(index);
                                                    setFeedback(null);
                                                }}
                                            >
                                                <span className="tv-admin-home-card-empty-label">Posicion {index + 1}</span>
                                                <strong>Elegir nota</strong>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {activeSlotIndex !== null ? (
                        <div className="tv-admin-home-library-backdrop" onClick={() => setActiveSlotIndex(null)}>
                            <section className="tv-admin-home-library" onClick={(event) => event.stopPropagation()}>
                                <div className="tv-admin-home-library-head">
                                    <h2>Seleccionar nota para posicion {activeSlotIndex + 1}</h2>

                                    <button
                                        type="button"
                                        className="tv-admin-home-library-close"
                                        aria-label="Cerrar"
                                        onClick={() => setActiveSlotIndex(null)}
                                    >
                                        &times;
                                    </button>
                                </div>

                                {libraryStatus === "loading" ? (
                                    <div className="tv-admin-fullscreen-spinner is-compact" aria-live="polite" aria-busy="true">
                                        <span className="tv-admin-spinner" aria-hidden="true"></span>
                                    </div>
                                ) : null}

                                {libraryStatus === "error" ? (
                                    <div className="tv-admin-feedback is-error">No pudimos cargar el listado de reemplazo.</div>
                                ) : null}

                                {libraryStatus !== "loading" && libraryItems.length ? (
                                    <>
                                        <div className="tv-admin-home-library-list">
                                            {libraryItems.map((item) => {
                                                const isUsed = draftItems.some((draftItem) => draftItem?.id === item.id);

                                                return (
                                                    <button
                                                        key={`${item.id}-${item.slug}`}
                                                        type="button"
                                                        className={`tv-admin-home-library-card${isUsed ? " is-current" : ""}`}
                                                        disabled={selectingCandidateId !== null}
                                                        onClick={() => handlePickReplacement(item)}
                                                    >
                                                        <span
                                                            className="tv-admin-home-library-category"
                                                            style={{ color: item.categoryColor || "#0a7e8c" }}
                                                        >
                                                            {formatLibraryCategoryLabel(item.category)}
                                                        </span>
                                                        <span className="tv-admin-home-library-copy">
                                                            <strong>{item.title}</strong>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {libraryHasMore ? (
                                            <div className="tv-admin-home-library-actions">
                                                <button
                                                    type="button"
                                                    className="tv-admin-secondary-btn"
                                                    onClick={loadMoreLibraryItems}
                                                    disabled={libraryStatus === "loading-more" || selectingCandidateId !== null}
                                                >
                                                    {libraryStatus === "loading-more" ? "Cargando..." : "Cargar mas"}
                                                </button>
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                            </section>
                        </div>
                    ) : null}

                    <div className="tv-admin-form-actions">
                        <button
                            type="button"
                            className="tv-admin-primary-btn"
                            onClick={handleSave}
                            disabled={saving || !canSave}
                        >
                            {saving ? "Guardando..." : "Guardar home"}
                        </button>
                    </div>
                </>
            ) : null} */}
        </AdminLayout>
    );
}
